import { Question } from '../../../../datasources/questions/QuestionsDataSource';
import {
  FlowCondition,
  FlowDefinition,
  FlowEdge,
  FlowNode,
} from '../../../../types/flow/Flow';
import { createNodeId } from './flowBuilder.helpers';

/**
 * O editor trabalha com uma lista linear: cada pergunta tem, no máximo, uma
 * condição sobre uma pergunta anterior. 
 */
export type StepConditionMode = 'is' | 'isNot';

export type StepCondition = {
  question: string;
  mode: StepConditionMode;
  /** `value` das opções da pergunta da condição. */
  values: string[];
};

export type FlowStep = {
  questionCode: string;
  condition?: StepCondition;
  unsupported?: boolean;
  notice?: string;
};

export type StepsValidation = {
  general?: string;
  steps: (string | undefined)[];
};

export function stepsToDefinition(steps: FlowStep[]): FlowDefinition {
  const nodes: FlowNode[] = [];
  steps.forEach((step) => {
    nodes.push({
      id: createNodeId(step.questionCode, nodes),
      type: 'QUESTION',
      question: step.questionCode,
    });
  });

  const edges: FlowEdge[] = [];

  steps.forEach((step, index) => {
    if (index === 0) return;

    const to = nodes[index].id;
    const condition = step.condition;
    const from =
      condition &&
      nodes
        .slice(0, index)
        .reverse()
        .find((node) => node.question === condition.question)?.id;

    // "Sempre" (ou condição incompleta): o caminho só liga à pergunta anterior.
    if (!condition || condition.values.length === 0 || !from) {
      edges.push({ from: nodes[index - 1].id, to });
      return;
    }

    const rules: FlowCondition[] = condition.values.map((value) => ({
      question: condition.question,
      operator: condition.mode === 'is' ? 'eq' : 'neq',
      value,
    }));

    edges.push({
      from,
      to,
      when:
        condition.mode === 'is' && rules.length > 1
          ? { any: rules }
          : { all: rules },
    });
  });

  return { schemaVersion: 2, nodes, edges };
}

function toCondition(
  edge: FlowEdge,
  nodes: FlowNode[],
  index: number
): StepCondition | undefined {
  const all = edge.when?.all;
  const any = edge.when?.any;
  const rules = all && !any ? all : any && !all ? any : undefined;
  if (!rules || rules.length === 0) return undefined;

  const question = rules[0].question;
  const sameQuestion = rules.every(
    (rule) => rule.question === question && typeof rule.value === 'string'
  );
  let triggerIndex = -1;
  for (let currentIndex = index - 1; currentIndex >= 0; currentIndex -= 1) {
    if (nodes[currentIndex].question === question) {
      triggerIndex = currentIndex;
      break;
    }
  }
  if (!sameQuestion || triggerIndex < 0 || triggerIndex >= index) {
    return undefined;
  }

  const values = rules.map((rule) => rule.value as string);
  const operators = new Set(rules.map((rule) => rule.operator));

  if (operators.size === 1 && operators.has('eq') && (any || rules.length === 1)) {
    return { question, mode: 'is', values };
  }
  if (operators.size === 1 && operators.has('neq') && all) {
    return { question, mode: 'isNot', values };
  }
  return undefined;
}

export function definitionToSteps(definition?: FlowDefinition): FlowStep[] {
  const nodes = definition?.nodes ?? [];
  const edges = definition?.edges ?? [];

  return nodes.map((node, index) => {
    const ruled = edges.filter((edge) => edge.to === node.id && edge.when);
    if (ruled.length === 0) return { questionCode: node.question };

    const condition =
      ruled.length === 1 ? toCondition(ruled[0], nodes, index) : undefined;
    return condition
      ? { questionCode: node.question, condition }
      : { questionCode: node.question, unsupported: true };
  });
}

/** Perguntas anteriores que podem ser usadas numa condição (precisam ter opções). */
export function eligibleTriggers(
  steps: FlowStep[],
  index: number,
  questionsByCode: Map<string, Question>
): Question[] {
  return steps
    .slice(0, index)
    .map((step) => questionsByCode.get(step.questionCode))
    .filter(
      (question): question is Question =>
        question !== undefined && question.options.length > 0
    )
    .filter(
      (question, questionIndex, eligibleQuestions) =>
        eligibleQuestions.findIndex((item) => item.code === question.code) ===
        questionIndex
    );
}

function joinLabels(labels: string[], conjunction: 'ou' | 'nem'): string {
  const quoted = labels.map((label) => `"${label}"`);
  if (quoted.length <= 1) return quoted.join('');
  return `${quoted.slice(0, -1).join(', ')} ${conjunction} ${quoted[quoted.length - 1]}`;
}

export function describeStep(
  step: FlowStep,
  questionsByCode: Map<string, Question>
): string {
  if (step.unsupported) {
    return 'Regra antiga não suportada. Escolha novamente quando mostrar.';
  }

  const condition = step.condition;
  if (!condition) return 'Mostrar sempre';

  const trigger = questionsByCode.get(condition.question);
  const triggerText = trigger?.text ?? condition.question;
  if (condition.values.length === 0) {
    return `Mostrar quando "${triggerText}"… (escolha as respostas)`;
  }

  const labels = condition.values.map(
    (value) =>
      trigger?.options.find((option) => option.value === value)?.label ?? value
  );
  return condition.mode === 'is'
    ? `Mostrar quando "${triggerText}" for ${joinLabels(labels, 'ou')}`
    : `Mostrar quando "${triggerText}" não for ${joinLabels(labels, 'nem')}`;
}

export function validateSteps(
  steps: FlowStep[],
  questionsByCode: Map<string, Question>
): StepsValidation {
  const result: StepsValidation = {
    general: steps.length === 0 ? 'Adicione pelo menos uma pergunta.' : undefined,
    steps: [],
  };

  result.steps = steps.map((step, index) => {
    if (!questionsByCode.has(step.questionCode)) {
      return 'Esta pergunta não existe mais. Remova-a do fluxo.';
    }
    if (
      steps
        .slice(0, index)
        .some((previous) => previous.questionCode === step.questionCode) &&
      !questionsByCode.get(step.questionCode)?.is_loop
    ) {
      return 'Esta pergunta não permite repetição no fluxo.';
    }
    if (step.unsupported) {
      return 'Escolha novamente quando esta pergunta deve aparecer.';
    }

    const condition = step.condition;
    if (!condition) return undefined;
    if (index === 0) return 'A primeira pergunta é sempre exibida.';

    const triggerIndex = steps.findIndex(
      (item) => item.questionCode === condition.question
    );
    if (triggerIndex < 0 || triggerIndex >= index) {
      return 'Escolha uma pergunta anterior para a condição.';
    }
    if (condition.values.length === 0) {
      return 'Escolha pelo menos uma resposta para a condição.';
    }

    const trigger = questionsByCode.get(condition.question);
    const optionValues = new Set(trigger?.options.map((option) => option.value));
    if (condition.values.some((value) => !optionValues.has(value))) {
      return 'Uma das respostas escolhidas não existe mais.';
    }
    return undefined;
  });

  return result;
}

export function isStepsValid(validation: StepsValidation): boolean {
  return !validation.general && validation.steps.every((error) => !error);
}

/** Limpa condições cuja pergunta deixou de vir antes (ou saiu do fluxo). */
export function sanitizeSteps(
  steps: FlowStep[],
  questionsByCode: Map<string, Question>
): FlowStep[] {
  return steps.map((step, index) => {
    const condition = step.condition;
    if (!condition) return step;

    const triggerIndex = steps.findIndex(
      (item) => item.questionCode === condition.question
    );
    if (triggerIndex >= 0 && triggerIndex < index) return step;

    const text = questionsByCode.get(condition.question)?.text ?? condition.question;
    return {
      questionCode: step.questionCode,
      notice:
        triggerIndex < 0
          ? `A condição foi removida porque "${text}" saiu do fluxo.`
          : `A condição foi removida porque "${text}" agora vem depois.`,
    };
  });
}

export function moveStep(
  steps: FlowStep[],
  from: number,
  to: number,
  questionsByCode: Map<string, Question>
): FlowStep[] {
  if (to < 0 || to >= steps.length) return steps;
  const next = [...steps];
  const [step] = next.splice(from, 1);
  next.splice(to, 0, step);
  return sanitizeSteps(next, questionsByCode);
}

export function removeStep(
  steps: FlowStep[],
  index: number,
  questionsByCode: Map<string, Question>
): FlowStep[] {
  return sanitizeSteps(
    steps.filter((_, current) => current !== index),
    questionsByCode
  );
}
